import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { ChartTooltipContext } from "./ChartTooltipContext";
import type { ChartTooltipPayloadItem } from "./ChartTooltipContext";
import { ChartTooltipLabel } from "./ChartTooltipLabel";
import { ChartTooltipRows } from "./ChartTooltipRows";

export interface ChartTooltipContentProps {
  /** Injected by recharts when passed as `Chart.Tooltip` content. */
  active?: boolean;
  /** Injected by recharts when passed as `Chart.Tooltip` content. */
  payload?: ChartTooltipPayloadItem[];
  /** Injected by recharts when passed as `Chart.Tooltip` content. */
  label?: unknown;
  className?: string;
  /** Composition of tooltip parts; omit for the default label + rows. */
  children?: ReactNode;
}

/**
 * Styled tooltip body for charts — pass as `content` of `Chart.Tooltip`. recharts clones this one
 * element and injects `active`/`payload`/`label` into it; the shell forwards that data through
 * context so the tooltip parts (`Chart.TooltipLabel`, `Chart.TooltipRows`, and the row parts
 * `Chart.TooltipIndicator`/`Chart.TooltipName`/`Chart.TooltipValue`) compose freely as children.
 * With no children it renders the default composition: a config-resolved header label plus one
 * indicator + name + value row per series.
 */
export const ChartTooltipContent = ({ active, payload, label, className, children }: ChartTooltipContentProps) => {
  if (!active || !payload?.length) return null;

  return (
    <ChartTooltipContext.Provider value={{ payload, label }}>
      <div
        data-slot="chart-tooltip-content"
        className={cn(
          "border-border/50 bg-background grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl",
          className
        )}
      >
        {children ?? (
          <>
            <ChartTooltipLabel />
            <ChartTooltipRows />
          </>
        )}
      </div>
    </ChartTooltipContext.Provider>
  );
};
